'use client';

import { CreateOrganizationDialog } from '@/components/organizations/CreateOrganizationDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuthUser } from '@/hooks/useAuthUser';
import { Link } from '@/i18n/routing';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import {
  fetchShareStatus,
  selectIsShared,
  selectShareLoading,
  shareSession as shareSessionThunk,
  unshareSession as unshareSessionThunk,
} from '@/lib/redux/slices/shareSlice';
import type { OrganizationData } from '@/services/organizations-service';
import { createClient } from '@/utils/supabase/client';
import {
  Copy,
  ExternalLink,
  Globe,
  Lock,
  Share,
  Share2,
  Users,
  X,
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

const TEAM_NUDGE_DISMISSED_KEY = 'lexomat_share_team_nudge_dismissed';

interface SharePopoverProps {
  children?: React.ReactNode | undefined;
  url?: string | undefined;
  title?: string | undefined;
  sessionId?: string | undefined;
}

type SharePermission = 'public' | 'private';

export function SharePopover({
  children,
  url = typeof window !== 'undefined' ? window.location.href : '',
  title,
  sessionId,
}: SharePopoverProps) {
  const { toast } = useToast();
  const { userId, isInOrg } = useAuthUser();
  const t = useTranslations('SharePopover');
  const dispatch = useAppDispatch();
  const locale = useLocale();

  // Redux state selectors
  const isShared = useAppSelector(selectIsShared(sessionId || ''));
  const isLoading = useAppSelector(selectShareLoading(sessionId || ''));

  // Local UI state
  const [, setCopied] = useState(false);
  const [sharePermission, setSharePermission] =
    useState<SharePermission>('private');
  const [isOpen, setIsOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [teamNudgeDismissed, setTeamNudgeDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(TEAM_NUDGE_DISMISSED_KEY) === 'true';
  });

  const handleDismissTeamNudge = useCallback(() => {
    localStorage.setItem(TEAM_NUDGE_DISMISSED_KEY, 'true');
    setTeamNudgeDismissed(true);
  }, []);

  const handleOrgCreated = useCallback(
    async (org: OrganizationData) => {
      setCreateOrgOpen(false);
      setIsOpen(false);
      const supabase = createClient();
      await supabase.auth.refreshSession();
      window.location.href = `/${locale}/org-onboarding/${org.org_id}?new=true`;
    },
    [locale]
  );

  // Fetch share status when component mounts (only once via Redux cache)
  useEffect(() => {
    if (sessionId) {
      dispatch(fetchShareStatus(sessionId));
    }
  }, [sessionId, dispatch]);

  // Update sharePermission when Redux state changes
  useEffect(() => {
    setSharePermission(isShared ? 'public' : 'private');
  }, [isShared]);

  const handleShare = async () => {
    if (!sessionId || !userId) {
      toast({
        title: t('error'),
        description: t('missingSessionOrUser'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await dispatch(shareSessionThunk({ sessionId })).unwrap();

      if (result.isShared) {
        toast({
          title: t('chatShared'),
          description: t('linkCopiedDescription'),
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Error sharing session:', error);
      toast({
        title: t('error'),
        description: t('failedToShare'),
        variant: 'destructive',
      });
    }
  };

  const handleUnshare = async () => {
    if (!sessionId || !userId) {
      toast({
        title: t('error'),
        description: t('missingSessionOrUser'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await dispatch(
        unshareSessionThunk({ sessionId })
      ).unwrap();

      if (!result.isShared) {
        setCopied(false);
        toast({
          title: t('chatUnshared'),
          description: t('shareLinkDisabled'),
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Error unsharing session:', error);
      toast({
        title: t('error'),
        description: t('failedToUnshare'),
        variant: 'destructive',
      });
    }
  };

  const handleCopyLink = async () => {
    const shareUrl = sessionId
      ? `${window.location.origin}/sk/resers/zdieľať/${sessionId}`
      : url;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: t('linkCopied'),
        description: t('linkCopiedDescription'),
        variant: 'success',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: t('failedToCopy'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const resetDialog = () => {
    setCopied(false);
  };

  const getSharePermissionIcon = (permission: SharePermission) => {
    switch (permission) {
      case 'public':
        return <Globe className='h-4 w-4' />;
      case 'private':
        return <Lock className='h-4 w-4' />;
      default:
        return <Lock className='h-4 w-4' />;
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            resetDialog();
          }
        }}
      >
        <DialogTrigger asChild>
          {children || (
            <Button variant='ghost' size='icon'>
              <Share className='w-4 h-4' />
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className='sm:max-w-md bg-surface-card'>
          {!isShared ? (
            // Unshared State - Initial Share Interface
            <>
              <DialogHeader>
                <DialogTitle className='text-lg font-semibold text-content-primary'>
                  {title || t('shareChat')}
                </DialogTitle>
              </DialogHeader>

              <div className='space-y-4'>
                {/* Share Permission Dropdown (Disabled) */}
                <div className='space-y-2'>
                  <div className='flex items-center gap-2 p-3 border border-line-default rounded-md bg-surface-muted cursor-not-allowed'>
                    <Lock className='h-4 w-4 text-content-muted' />
                    <span className='text-sm text-content-secondary'>
                      {t('privateOnlyYouHaveAccess')}
                    </span>
                  </div>
                </div>

                {/* Warning Text */}
                <div className='flex items-start gap-2 p-3 bg-surface-muted rounded-md border border-line-default'>
                  <div className='text-content-muted mt-0.5'>
                    <svg
                      className='h-4 w-4'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <div className='text-sm text-content-secondary'>
                    {t('dontSharePersonalInfo')}{' '}
                    <Link
                      href='/terms-of-use'
                      className='underline hover:no-underline text-brand-primary-color'
                      target='_blank'
                    >
                      {t('termsOfUse')}
                    </Link>{' '}
                    {t('and')}{' '}
                    <Link
                      href='/privacy-policy'
                      className='underline hover:no-underline text-brand-primary-color'
                      target='_blank'
                    >
                      {t('privacyPolicy')}
                    </Link>
                    .
                  </div>
                </div>

                {/* Share Button */}
                <Button
                  onClick={handleShare}
                  disabled={isLoading || !sessionId || !userId}
                  className='bg-brand-primary-color text-white hover:bg-brand-primary-hover rounded-full px-6 py-2 text-sm font-normal w-auto disabled:opacity-50'
                >
                  <Share2 className='h-4 w-4 mr-2' />
                  {isLoading ? t('sharing') : t('share')}
                </Button>
              </div>
            </>
          ) : (
            // Shared State - Full Interface
            <>
              <DialogHeader>
                <DialogTitle className='text-lg font-semibold text-content-primary'>
                  {t('chatShared')}
                </DialogTitle>
              </DialogHeader>

              <div className='space-y-4'>
                {/* Share Permission Dropdown */}
                <div className='space-y-2'>
                  <Select
                    value={sharePermission}
                    disabled={isLoading}
                    onValueChange={async (value: SharePermission) => {
                      if (value === 'private') {
                        // When switching to private, unshare via API
                        await handleUnshare();
                      } else {
                        setSharePermission(value);
                      }
                    }}
                  >
                    <SelectTrigger className='w-full'>
                      <div className='flex items-center gap-2'>
                        {getSharePermissionIcon(sharePermission)}
                        <SelectValue placeholder={t('selectSharePermission')} />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='public'>
                        {t('publicAnyoneCanView')}
                      </SelectItem>
                      <SelectItem value='private'>
                        {t('privateOnlyYouHaveAccess')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* URL Input Field */}
                <div className='space-y-2'>
                  <div className='relative'>
                    <Input
                      readOnly
                      value={
                        sessionId
                          ? `${window.location.origin}/sk/resers/zdieľať/${sessionId}`
                          : ''
                      }
                      className='bg-surface-muted border-line-default text-sm text-content-primary pr-8'
                      onClick={(e) => {
                        e.currentTarget.select();
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    />
                    <ExternalLink
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 cursor-pointer text-content-muted hover:text-content-primary'
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const shareUrl = sessionId
                          ? `${window.location.origin}/sk/resers/zdieľať/${sessionId}`
                          : '';
                        window.open(shareUrl, '_blank');
                      }}
                    />
                  </div>
                </div>

                {/* Warning Text */}
                <div className='flex items-start gap-2 p-3 bg-surface-muted rounded-md border border-line-default'>
                  <div className='text-content-muted mt-0.5'>
                    <svg
                      className='h-4 w-4'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <div className='text-sm text-content-secondary'>
                    {t('dontSharePersonalInfo')}{' '}
                    <a
                      href='/terms-of-use'
                      className='underline hover:no-underline text-brand-primary-color'
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('termsOfUse')}
                    </a>{' '}
                    {t('and')}{' '}
                    <a
                      href='/privacy-policy'
                      className='underline hover:no-underline text-brand-primary-color'
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('privacyPolicy')}
                    </a>
                    .
                  </div>
                </div>

                {/* Copy Link Button */}
                <Button
                  onClick={handleCopyLink}
                  disabled={isLoading}
                  className='bg-brand-primary-color text-white hover:bg-brand-primary-hover rounded-full px-6 py-2 text-sm font-normal w-auto disabled:opacity-50'
                >
                  <Copy className='h-4 w-4 mr-2' />
                  {t('copyLink')}
                </Button>

                {/* Team collaboration nudge — solo users only */}
                {!isInOrg && !teamNudgeDismissed && (
                  <div className='relative flex items-start gap-2.5 p-3 rounded-md border border-line-subtle bg-surface-elevated'>
                    <Users className='h-4 w-4 text-brand-tertiary shrink-0 mt-0.5' />
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm font-medium text-content-primary'>
                        {t('teamNudgeTitle')}
                      </p>
                      <p className='text-xs text-content-muted mt-0.5 mb-2'>
                        {t('teamNudgeDesc')}
                      </p>
                      <button
                        onClick={() => setCreateOrgOpen(true)}
                        className='text-xs font-medium text-brand-tertiary hover:text-brand-tertiary-hover transition-colors'
                      >
                        {t('teamNudgeCta')}
                      </button>
                    </div>
                    <button
                      onClick={handleDismissTeamNudge}
                      className='shrink-0 p-2 -m-1 text-content-muted hover:text-content-secondary transition-colors rounded-sm'
                      aria-label={t('teamNudgeDismiss')}
                    >
                      <X className='h-3.5 w-3.5' />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Organization Dialog — must be outside the share Dialog to avoid nested Radix context issues */}
      <CreateOrganizationDialog
        open={createOrgOpen}
        onOpenChange={setCreateOrgOpen}
        onOrgCreated={handleOrgCreated}
      />
    </>
  );
}
